// eslint-disable-next-line no-restricted-imports
import { ResizeObserver } from '@juggle/resize-observer';
import { AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import type { CSSProperties, ReactNode } from 'react';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import ZoomInAnimation from '../../../components/ZoomInAnimation';
import { isTouchDevice } from '../../../constants/os';
import ClickOutsideHandler from '../../../utils/ClickOutsideHandler';
import { READONLY_CHANGE_EVENT } from '../../constants';
import { useQuill } from '../../QuillContext';
import type { Range } from '../../types';
import styles from './ToolbarPopOver.module.scss';

const getToolbarBoundsForMultilineSelection = (quill: Quill, range: Range) => {
  // Only consider nearest line in tooltip bounds
  if (isTouchDevice) {
    const [, offset] = quill.getLine(range.index + range.length);
    return quill.getBounds(range.index + range.length - offset, offset);
  }
  const [line, offset] = quill.getLine(range.index);
  return line ? quill.getBounds(range.index, line.length() - offset - 1) : null;
};

const useToolbarOpen = (quill: Quill | null) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!quill) return undefined;
    if (quill.getSelection()) setIsOpen(true);

    const selectionListener = (range: Range) => {
      if (range) setIsOpen(true);
    };

    const textListener = (delta: Delta, _old: unknown, source: string) => {
      if (
        source === Quill.sources.USER &&
        delta.ops.some((op) => op.insert || op.delete)
      ) {
        setIsOpen(false);
      }
    };

    // When user clicks on the post mode switcher
    const readOnlyListener = () => {
      const range = quill.getSelection();
      if (range) setIsOpen(true);
    };

    quill.on(Quill.events.SELECTION_CHANGE, selectionListener);
    quill.on(Quill.events.TEXT_CHANGE, textListener);
    quill.on(READONLY_CHANGE_EVENT, readOnlyListener);
    return () => {
      quill.off(Quill.events.SELECTION_CHANGE, selectionListener);
      quill.off(Quill.events.TEXT_CHANGE, textListener);
      quill.off(READONLY_CHANGE_EVENT, readOnlyListener);
    };
  }, [quill]);

  return { isOpen, closeToolbar: () => setIsOpen(false) };
};

interface ToolbarPopOverProps {
  children: (state: {
    lines: unknown[];
    range: Range;
    isInTitleLine: boolean;
    closeToolbar: () => void;
  }) => ReactNode;
  portal?: HTMLElement | null;
  showInTitleLine?: boolean;
}

const ToolbarPopOver = ({
  portal,
  children,
  showInTitleLine,
}: ToolbarPopOverProps) => {
  const {
    quill,
    selection: { range },
  } = useQuill();

  const [isInTitleLine, lines] = useMemo<[boolean, unknown[] | null]>(() => {
    if (!quill || !range) return [false, null];
    const isTitleFocused = quill.isTitleFocused();
    if (!showInTitleLine && isTitleFocused) return [true, null];
    return [isTitleFocused, quill.getLines(range)];
  }, [quill, range, showInTitleLine]);

  const referencePositionStyles = useMemo<CSSProperties | null>(() => {
    if (!lines || !range || !quill) return null;

    const bounds =
      lines.length > 1
        ? getToolbarBoundsForMultilineSelection(quill, range)
        : quill.getBounds(range);

    if (!bounds) return null;

    // Make sure the reference position is inside editor
    const left = Math.max(bounds.left, 0);
    const right = Math.min(
      bounds.right,
      quill.container.getBoundingClientRect().width,
    );

    return {
      left,
      width: right - left,
      top: bounds.top,
      height: bounds.height,
    };
  }, [quill, range, lines]);

  const { isOpen, closeToolbar } = useToolbarOpen(quill);

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);

  const updateRef = useRef<(() => void) | null>(null);
  const {
    styles: { popper },
    attributes,
    update,
  } = usePopper(referenceElement, popperElement, {
    // For touch devices, putting the toolbar underneath to avoid overlapping
    // with the system context menu.
    placement: isTouchDevice ? 'bottom' : 'top',
    modifiers: [
      { name: 'preventOverflow', options: { padding: 10 } },
      {
        name: 'flip',
        options: { padding: 10 },
      },
      { name: 'offset', options: { offset: [0, isTouchDevice ? 16 : 10] } },
    ],
  });

  // `update` can be null when popper is not ready.
  updateRef.current = update;

  // reposition when selection bounds changed
  useEffect(() => {
    updateRef.current?.();
  }, [referencePositionStyles]);

  // reposition when toolbar content resized
  useLayoutEffect(() => {
    if (!popperElement) return undefined;
    const resizeObserver = new ResizeObserver(() => updateRef.current?.());
    resizeObserver.observe(popperElement);
    return () => resizeObserver.disconnect();
  }, [popperElement]);

  const content =
    referencePositionStyles &&
    isOpen &&
    range &&
    lines &&
    children({ lines, range, isInTitleLine, closeToolbar });

  const toolbar = (
    <ClickOutsideHandler
      data-no-print
      onClickOutside={(e) => {
        const clickedBlot =
          e.target instanceof Element && quill?.scroll.find(e.target);

        if (!clickedBlot) {
          closeToolbar();
        }
      }}
    >
      <div
        className={styles.toolbarContainer}
        ref={content ? setPopperElement : undefined}
        style={popper}
        {...attributes.popper}
      >
        <AnimatePresence>
          {!!content && (
            <ZoomInAnimation
              initial={{
                transformOrigin: 'bottom',
              }}
              className={styles.toolbar}
              role="toolbar"
            >
              {content}
            </ZoomInAnimation>
          )}
        </AnimatePresence>
      </div>
    </ClickOutsideHandler>
  );

  return (
    <>
      {!!content && referencePositionStyles && (
        <div
          ref={setReferenceElement}
          className={styles.reference}
          style={referencePositionStyles}
        />
      )}
      {portal ? createPortal(toolbar, portal) : toolbar}
    </>
  );
};

ToolbarPopOver.propTypes = {
  portal: PropTypes.instanceOf(Element),
  boundary: PropTypes.instanceOf(Element),
  children: PropTypes.func.isRequired,
  showInTitleLine: PropTypes.bool,
};

ToolbarPopOver.defaultProps = {
  portal: null,
  boundary: undefined,
  showInTitleLine: false,
};

export default ToolbarPopOver;
